import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { RadioChangeEvent } from 'antd';
import {
  EyeOutlined,
  EditOutlined,
  MinusCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  type ServiceProviderAccountStatus,
  type ServiceProviderCertificationStatus,
  type ServiceProviderDetail,
  type ServiceProviderListItem,
  type ServiceProviderListStats,
  deductServiceProviderDeposit,
  getServiceProviderDetail,
  listServiceProviders,
  updateServiceProvider,
  updateServiceProviderStatus,
} from '../../services/serviceProviderAccountService';
import {
  listServiceProviderSettlements,
  listServiceProviderWithdrawRecords,
  type ServiceProviderSettlementListItem,
  type ServiceProviderWithdrawRecordListItem,
} from '../../services/serviceProviderDetailFlowService';
import { normalizeFileUrl } from '../../services/fileService';
import { useHorizontalTableWheel } from '../../hooks/useHorizontalTableWheel';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const accountStatusOptions = [
  { label: '全部', value: 'all' as const },
  { label: '正常', value: 'active' as const },
  { label: '禁用', value: 'inactive' as const },
];

const certificationStatusOptions = [
  { label: '全部', value: 'all' as const },
  { label: '待审核', value: 'reviewing' as const },
  { label: '已通过', value: 'approved' as const },
  { label: '已驳回', value: 'rejected' as const },
];

const securityDepositOptions = [
  { label: '全部', value: 'all' as const },
  { label: '已缴纳', value: 'paid' as const },
  { label: '未缴纳', value: 'unpaid' as const },
];

const certificationStatusMap: Record<ServiceProviderCertificationStatus, { color: string; text: string }> = {
  reviewing: { color: 'processing', text: '待审核' },
  approved: { color: 'success', text: '已通过' },
  rejected: { color: 'error', text: '已驳回' },
};

const accountStatusMap: Record<string, { color: string; text: string }> = {
  active: { color: 'success', text: '正常' },
  inactive: { color: 'default', text: '禁用' },
};

const settlementStatusMap: Record<string, { color: string; text: string }> = {
  pending: { color: 'processing', text: '待结算' },
  settled: { color: 'success', text: '已结算' },
};

const withdrawStatusMap: Record<string, { color: string; text: string }> = {
  pending_review: { color: 'processing', text: '待审核' },
  processing: { color: 'processing', text: '处理中' },
  pending_receive: { color: 'warning', text: '待收款' },
  paid: { color: 'success', text: '已到账' },
  rejected: { color: 'error', text: '已驳回' },
  failed: { color: 'error', text: '提现失败' },
  canceled: { color: 'default', text: '已取消' },
};

const moneyFormatter = (value?: number) =>
  `￥${Number(value || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const resolveErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object') {
    if ('response' in error) {
      const response = (error as { response?: { data?: { message?: unknown } } }).response;
      const messageText = response?.data?.message;
      if (typeof messageText === 'string' && messageText.trim()) {
        return messageText;
      }
    }
    if ('message' in error) {
      const messageText = (error as { message?: unknown }).message;
      if (typeof messageText === 'string' && messageText.trim()) {
        return messageText;
      }
    }
  }
  return fallback;
};

const StaffManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [items, setItems] = useState<ServiceProviderListItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [stats, setStats] = useState<ServiceProviderListStats>({
    totalCount: 0,
    certifiedCount: 0,
    uncertifiedCount: 0,
    depositPaidCount: 0,
    depositUnpaidCount: 0,
  });
  const [detailOpen, setDetailOpen] = useState(false);
  const [currentDetail, setCurrentDetail] = useState<ServiceProviderDetail | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editingDetail, setEditingDetail] = useState<ServiceProviderDetail | null>(null);
  const [deductOpen, setDeductOpen] = useState(false);
  const [deductSubmitting, setDeductSubmitting] = useState(false);
  const [deductRecord, setDeductRecord] = useState<ServiceProviderListItem | null>(null);
  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
  const [settlementOpen, setSettlementOpen] = useState(false);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementRecord, setSettlementRecord] = useState<ServiceProviderListItem | null>(null);
  const [settlementItems, setSettlementItems] = useState<ServiceProviderSettlementListItem[]>([]);
  const [settlementPagination, setSettlementPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawRecord, setWithdrawRecord] = useState<ServiceProviderListItem | null>(null);
  const [withdrawItems, setWithdrawItems] = useState<ServiceProviderWithdrawRecordListItem[]>([]);
  const [withdrawPagination, setWithdrawPagination] = useState({ page: 1, pageSize: 10, total: 0 });

  const [searchForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const tableWrapRef = useHorizontalTableWheel();
  const [deductForm] = Form.useForm();
  const [settlementSearchForm] = Form.useForm();
  const [withdrawSearchForm] = Form.useForm();

  const loadData = async (page = pagination.page, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const values = searchForm.getFieldsValue();
      const accountStatus = values.accountStatus === 'all' ? undefined : values.accountStatus;
      const certificationStatus = values.certificationStatus || 'all';
      const securityDepositPaid = values.securityDepositPaid === 'all' ? undefined : values.securityDepositPaid;
      const res = await listServiceProviders({
        page,
        pageSize,
        name: values.name || undefined,
        phone: values.phone || undefined,
        address: values.address || undefined,
        serviceProject: values.serviceProject || undefined,
        certificationStatus,
        securityDepositPaid,
        accountStatus,
      });
      setItems(res.list || []);
      setStats({
        totalCount: res.stats?.totalCount || res.total || 0,
        certifiedCount: res.stats?.certifiedCount || 0,
        uncertifiedCount: res.stats?.uncertifiedCount || 0,
        depositPaidCount: res.stats?.depositPaidCount || 0,
        depositUnpaidCount: res.stats?.depositUnpaidCount || 0,
      });
      setPagination({
        page,
        pageSize,
        total: res.total || 0,
      });
    } catch {
      message.error('加载服务人员列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        const res = await listServiceProviders({ page: 1, pageSize: 10, certificationStatus: 'all' });
        setItems(res.list || []);
        setStats({
          totalCount: res.stats?.totalCount || res.total || 0,
          certifiedCount: res.stats?.certifiedCount || 0,
          uncertifiedCount: res.stats?.uncertifiedCount || 0,
          depositPaidCount: res.stats?.depositPaidCount || 0,
          depositUnpaidCount: res.stats?.depositUnpaidCount || 0,
        });
        setPagination({
          page: 1,
          pageSize: 10,
          total: res.total || 0,
        });
      } catch {
        message.error('加载服务人员列表失败');
      } finally {
        setLoading(false);
      }
    };

    void initialize();
  }, []);

  const handleSearch = () => {
    loadData(1, pagination.pageSize);
  };

  const handleAccountStatusChange = (event: RadioChangeEvent) => {
    searchForm.setFieldsValue({ accountStatus: event.target.value || 'all' });
    loadData(1, pagination.pageSize);
  };

  const handleReset = () => {
    searchForm.setFieldsValue({
      name: undefined,
      phone: undefined,
      address: undefined,
      serviceProject: undefined,
      certificationStatus: 'all',
      securityDepositPaid: 'all',
      accountStatus: 'all',
    });
    loadData(1, pagination.pageSize);
  };

  const loadDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const detail = await getServiceProviderDetail(id);
      setCurrentDetail(detail);
      setDetailOpen(true);
    } catch {
      message.error('加载服务人员详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const openEdit = async (id: string) => {
    setDetailLoading(true);
    try {
      const detail = await getServiceProviderDetail(id);
      setEditingDetail(detail);
      editForm.setFieldsValue({
        name: detail.name || '',
        phone: detail.phone || '',
        levelName: detail.levelName || '',
        serviceProvince: detail.serviceProvince || '',
        serviceCity: detail.serviceCity || '',
        serviceDistrict: detail.serviceDistrict || '',
        serviceAddressDetail: detail.serviceAddressDetail || '',
        serviceExperience: detail.serviceExperience || '',
        serviceProjects: detail.serviceProjectsText || '',
        petTypes: detail.petTypes || '',
        specialAbilities: detail.specialAbilities || '',
        certificationStatus: detail.certificationStatus || 'approved',
        accountStatus: detail.accountStatus || 'active',
        securityDepositPaid: detail.securityDepositPaid,
        orderStartTime: detail.orderStartTime || '08:00',
        orderEndTime: detail.orderEndTime || '21:00',
        bio: detail.bio || '',
        notifyEnabled: detail.notifyEnabled,
        orderMode: detail.orderMode,
        autoAcceptOrder: detail.autoAcceptOrder,
      });
      setEditOpen(true);
    } catch {
      message.error('加载服务人员编辑信息失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!editingDetail) {
      return;
    }
    try {
      const values = await editForm.validateFields();
      setEditSubmitting(true);
      const detail = await updateServiceProvider(editingDetail.id, {
        ...values,
        name: values.name || '',
        phone: values.phone || '',
        levelName: values.levelName || '',
        serviceProvince: values.serviceProvince || '',
        serviceCity: values.serviceCity || '',
        serviceDistrict: values.serviceDistrict || '',
        serviceAddressDetail: values.serviceAddressDetail || '',
        serviceExperience: values.serviceExperience || '',
        serviceProjects: values.serviceProjects || '',
        petTypes: values.petTypes || '',
        specialAbilities: values.specialAbilities || '',
        certificationStatus: values.certificationStatus || 'approved',
        accountStatus: values.accountStatus || 'active',
        securityDepositPaid: Boolean(values.securityDepositPaid),
        orderStartTime: values.orderStartTime || '08:00',
        orderEndTime: values.orderEndTime || '21:00',
        bio: values.bio || '',
        notifyEnabled: Boolean(values.notifyEnabled),
        orderMode: Boolean(values.orderMode),
        autoAcceptOrder: Boolean(values.autoAcceptOrder),
      });
      if (currentDetail?.id === detail.id) {
        setCurrentDetail(detail);
      }
      message.success('服务人员信息已保存');
      setEditOpen(false);
      setEditingDetail(null);
      editForm.resetFields();
      loadData();
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return;
      }
      message.error('保存服务人员信息失败');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleToggleStatus = async (record: { id: string; accountStatus: ServiceProviderAccountStatus | string }) => {
    const nextStatus: ServiceProviderAccountStatus = record.accountStatus === 'active' ? 'inactive' : 'active';
    setStatusSubmitting(true);
    try {
      const detail = await updateServiceProviderStatus(record.id, nextStatus);
      if (currentDetail?.id === record.id) {
        setCurrentDetail(detail);
      }
      message.success(nextStatus === 'inactive' ? '服务人员已禁用' : '服务人员已启用');
      loadData();
    } catch {
      message.error(nextStatus === 'inactive' ? '禁用失败' : '启用失败');
    } finally {
      setStatusSubmitting(false);
    }
  };

  const openDepositDeduct = (record: ServiceProviderListItem) => {
    setDeductRecord(record);
    deductForm.setFieldsValue({
      amount: undefined,
      title: '扣除保证金',
      remark: '后台扣除保证金',
    });
    setDeductOpen(true);
  };

  const handleDepositDeductSubmit = async () => {
    if (!deductRecord) {
      return;
    }
    try {
      const values = await deductForm.validateFields();
      setDeductSubmitting(true);
      const detail = await deductServiceProviderDeposit(deductRecord.id, {
        amount: Number(values.amount || 0),
        title: values.title || '',
        remark: values.remark || '',
      });
      if (currentDetail?.id === detail.id) {
        setCurrentDetail(detail);
      }
      message.success('保证金已扣除');
      setDeductOpen(false);
      setDeductRecord(null);
      deductForm.resetFields();
      loadData();
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return;
      }
      message.error(resolveErrorMessage(error, '扣除保证金失败'));
    } finally {
      setDeductSubmitting(false);
    }
  };

  const loadSettlementData = async (record: ServiceProviderListItem, page = 1, pageSize = settlementPagination.pageSize) => {
    setSettlementLoading(true);
    try {
      const values = settlementSearchForm.getFieldsValue();
      const dateRange = values.dateRange as { format?: (formatStr: string) => string }[] | undefined;
      const response = await listServiceProviderSettlements(record.id, {
        page,
        pageSize,
        orderNo: values.orderNo || undefined,
        customerName: values.customerName || undefined,
        customerPhone: values.customerPhone || undefined,
        startDate: dateRange?.[0]?.format?.('YYYY-MM-DD'),
        endDate: dateRange?.[1]?.format?.('YYYY-MM-DD'),
      });
      setSettlementItems(response.list || []);
      setSettlementPagination({
        page,
        pageSize,
        total: response.total || 0,
      });
    } catch {
      message.error('加载结算详情失败');
    } finally {
      setSettlementLoading(false);
    }
  };

  const openSettlementDetail = (record: ServiceProviderListItem) => {
    setSettlementRecord(record);
    settlementSearchForm.resetFields();
    setSettlementOpen(true);
    void loadSettlementData(record, 1, settlementPagination.pageSize);
  };

  const loadWithdrawData = async (record: ServiceProviderListItem, page = 1, pageSize = withdrawPagination.pageSize) => {
    setWithdrawLoading(true);
    try {
      const values = withdrawSearchForm.getFieldsValue();
      const dateRange = values.dateRange as { format?: (formatStr: string) => string }[] | undefined;
      const response = await listServiceProviderWithdrawRecords(record.id, {
        page,
        pageSize,
        startDate: dateRange?.[0]?.format?.('YYYY-MM-DD'),
        endDate: dateRange?.[1]?.format?.('YYYY-MM-DD'),
      });
      setWithdrawItems(response.list || []);
      setWithdrawPagination({
        page,
        pageSize,
        total: response.total || 0,
      });
    } catch {
      message.error('加载提现详情失败');
    } finally {
      setWithdrawLoading(false);
    }
  };

  const openWithdrawDetail = (record: ServiceProviderListItem) => {
    setWithdrawRecord(record);
    withdrawSearchForm.resetFields();
    setWithdrawOpen(true);
    void loadWithdrawData(record, 1, withdrawPagination.pageSize);
  };

  const renderCertificationStatus = (status: ServiceProviderCertificationStatus) => {
    const meta = certificationStatusMap[status] || { color: 'default', text: status };
    return <Tag color={meta.color}>{meta.text}</Tag>;
  };

  const renderAccountStatus = (status: string) => {
    const meta = accountStatusMap[status] || { color: 'default', text: status || '-' };
    return <Tag color={meta.color}>{meta.text}</Tag>;
  };

  const renderSettlementStatus = (status: string) => {
    const meta = settlementStatusMap[status] || { color: 'default', text: status || '-' };
    return <Tag color={meta.color}>{meta.text}</Tag>;
  };

  const renderWithdrawStatus = (status: string) => {
    const meta = withdrawStatusMap[status] || { color: 'default', text: status || '-' };
    return <Tag color={meta.color}>{meta.text}</Tag>;
  };

  const renderProjects = (projects: string[], fallbackText?: string) => {
    if (projects?.length) {
      return (
        <Space size={[0, 6]} wrap>
          {projects.map((item) => (
            <Tag key={item}>{item}</Tag>
          ))}
        </Space>
      );
    }
    return <Text type="secondary">{fallbackText || '-'}</Text>;
  };

  const renderTextTags = (rawText?: string) => {
    const values = Array.from(
      new Set(
        String(rawText || '')
          .split(/[,\uff0c\u3001;\uff1b\r\n\t ]+/)
          .map((item) => item.trim())
          .filter(Boolean),
      ),
    );

    if (!values.length) {
      return <Text type="secondary">-</Text>;
    }

    return (
      <Space size={[0, 6]} wrap>
        {values.map((item) => (
          <Tag key={item}>{item}</Tag>
        ))}
      </Space>
    );
  };

  const renderImageCard = (title: string, url?: string) => (
    <div style={{ width: 180 }}>
      <div style={{ marginBottom: 8, fontWeight: 500 }}>{title}</div>
      {url ? (
        <Image
          src={normalizeFileUrl(url)}
          width={180}
          height={120}
          style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid #f0f0f0' }}
        />
      ) : (
        <div
          style={{
            height: 120,
            border: '1px dashed #d9d9d9',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
          }}
        >
          暂无图片
        </div>
      )}
    </div>
  );

  const openVideoPreview = (url?: string) => {
    const normalized = normalizeFileUrl(url);
    if (!normalized) {
      return;
    }
    setVideoPreviewUrl(normalized);
    setVideoPreviewOpen(true);
  };

  const renderVideoCard = (title: string, url?: string) => {
    const normalizedUrl = normalizeFileUrl(url);
    return (
      <div style={{ width: 180 }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>{title}</div>
        {normalizedUrl ? (
          <div
            onClick={() => openVideoPreview(url)}
            style={{
              width: 180,
              height: 120,
              borderRadius: 8,
              border: '1px solid #f0f0f0',
              overflow: 'hidden',
              position: 'relative',
              cursor: 'pointer',
              background: '#000',
            }}
          >
            <video
              src={normalizedUrl}
              muted
              preload="metadata"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0, 0, 0, 0.24)',
                color: '#fff',
                fontSize: 28,
              }}
            >
              <PlayCircleOutlined />
            </div>
          </div>
        ) : (
          <div
            style={{
              height: 120,
              border: '1px dashed #d9d9d9',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
            }}
          >
            暂无视频
          </div>
        )}
      </div>
    );
  };

  const columns: ColumnsType<ServiceProviderListItem> = [
    {
      title: '服务人员',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      render: (_, record) => (
        <Space>
          <Avatar src={record.avatar ? normalizeFileUrl(record.avatar) : undefined}>
            {record.name?.slice(0, 1)}
          </Avatar>
          <div>
            <div>{record.name || '-'}</div>
            <Text type="secondary">{record.username || '-'}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: (value: string) => value || '-',
    },
    {
      title: '服务区域',
      dataIndex: 'serviceRegion',
      key: 'serviceRegion',
      width: 180,
      render: (value: string) => value || '-',
    },
    {
      title: '服务项目',
      dataIndex: 'serviceProjects',
      key: 'serviceProjects',
      width: 240,
      render: (value: string[], record) => renderProjects(value, record.serviceProjectsText),
    },
    {
      title: '认证状态',
      dataIndex: 'certificationStatus',
      key: 'certificationStatus',
      width: 100,
      render: renderCertificationStatus,
    },
    {
      title: '账号状态',
      dataIndex: 'accountStatus',
      key: 'accountStatus',
      width: 100,
      render: renderAccountStatus,
    },
    {
      title: '保证金',
      dataIndex: 'securityDepositPaid',
      key: 'securityDepositPaid',
      width: 170,
      render: (_, record) => (
        <div>
          <Tag color={record.securityDepositPaid ? 'success' : 'default'}>
            {record.securityDepositPaid ? '已缴纳' : '未缴纳'}
          </Tag>
          <div style={{ marginTop: 6 }}>
            <Text type="secondary">可退 {moneyFormatter(record.securityDepositRefundableAmount)}</Text>
          </div>
        </div>
      ),
    },
    {
      title: '余额',
      dataIndex: 'withdrawableAmount',
      key: 'withdrawableAmount',
      width: 120,
      render: (value: number) => moneyFormatter(value),
    },
    {
      title: '已提现',
      dataIndex: 'withdrawnAmount',
      key: 'withdrawnAmount',
      width: 120,
      render: (value: number) => moneyFormatter(value),
    },
    {
      title: '最后登录',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 370,
      fixed: 'right',
      render: (_, record) => {
        const isActive = record.accountStatus === 'active';
        return (
          <Space>
            <Button size="small" icon={<EyeOutlined />} onClick={() => loadDetail(record.id)}>
              详情
            </Button>
            <Button size="small" onClick={() => openSettlementDetail(record)}>
              结算详情
            </Button>
            <Button size="small" onClick={() => openWithdrawDetail(record)}>
              提现详情
            </Button>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record.id)}>
              编辑
            </Button>
            <Button
              size="small"
              danger
              icon={<MinusCircleOutlined />}
              onClick={() => openDepositDeduct(record)}
            >
              保证金扣除
            </Button>
            <Popconfirm
              title={isActive ? '确定禁用该服务人员吗？' : '确定启用该服务人员吗？'}
              onConfirm={() => handleToggleStatus(record)}
              okButtonProps={{ loading: statusSubmitting }}
            >
              <Button size="small" danger={isActive} icon={<StopOutlined />}>
                {isActive ? '禁用' : '启用'}
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const settlementColumns: ColumnsType<ServiceProviderSettlementListItem> = [
    { title: '主订单号', dataIndex: 'orderNo', key: 'orderNo', width: 180 },
    { title: '子订单号', dataIndex: 'subOrderNo', key: 'subOrderNo', width: 190 },
    { title: '客户姓名', dataIndex: 'contactName', key: 'contactName', width: 120, render: (value: string) => value || '-' },
    { title: '客户电话', dataIndex: 'contactPhone', key: 'contactPhone', width: 140, render: (value: string) => value || '-' },
    { title: '服务内容', dataIndex: 'serviceContent', key: 'serviceContent', width: 180, render: (value: string) => value || '-' },
    { title: '服务日期', dataIndex: 'serviceDate', key: 'serviceDate', width: 120, render: (value: string) => value || '-' },
    { title: '结算金额', dataIndex: 'settlementAmount', key: 'settlementAmount', width: 120, render: (value: number) => moneyFormatter(value) },
    { title: '状态', dataIndex: 'status', key: 'status', width: 100, render: renderSettlementStatus },
    { title: '完成时间', dataIndex: 'completedAt', key: 'completedAt', width: 170, render: (value: string) => value || '-' },
    { title: '结算时间', dataIndex: 'settledAt', key: 'settledAt', width: 170, render: (value: string) => value || '-' },
  ];

  const withdrawColumns: ColumnsType<ServiceProviderWithdrawRecordListItem> = [
    { title: '提现金额', dataIndex: 'amount', key: 'amount', width: 120, render: (value: number) => moneyFormatter(value) },
    { title: '提现方式', dataIndex: 'methodText', key: 'methodText', width: 120, render: (value: string, record) => value || record.method || '-' },
    { title: '收款姓名', dataIndex: 'accountName', key: 'accountName', width: 140, render: (value: string) => value || '-' },
    { title: '收款账号', dataIndex: 'accountNo', key: 'accountNo', width: 180, render: (value: string) => value || '-' },
    { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: renderWithdrawStatus },
    { title: '申请前余额', dataIndex: 'beforeBalance', key: 'beforeBalance', width: 120, render: (value: number) => moneyFormatter(value) },
    { title: '申请后余额', dataIndex: 'afterBalance', key: 'afterBalance', width: 120, render: (value: number) => moneyFormatter(value) },
    { title: '申请时间', dataIndex: 'requestedAt', key: 'requestedAt', width: 170, render: (value: string) => value || '-' },
    { title: '审核时间', dataIndex: 'reviewedAt', key: 'reviewedAt', width: 170, render: (value: string) => value || '-' },
    { title: '到账时间', dataIndex: 'arrivedAt', key: 'arrivedAt', width: 170, render: (value: string) => value || '-' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={(
          <>
            <TeamOutlined /> 服务人员
          </>
        )}
        extra={(
          <Button icon={<ReloadOutlined />} onClick={() => loadData()}>
            刷新
          </Button>
        )}
      >
        <Form
          form={searchForm}
          layout="inline"
          initialValues={{ accountStatus: 'all', certificationStatus: 'all', securityDepositPaid: 'all' }}
          style={{ marginBottom: 16, rowGap: 12 }}
        >
          <Form.Item name="name" label="姓名">
            <Input allowClear placeholder="请输入姓名" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="phone" label="手机号">
            <Input allowClear placeholder="请输入手机号" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="address" label="地址">
            <Input allowClear placeholder="省 / 市 / 区 / 详细地址" style={{ width: 220 }} />
          </Form.Item>
          <Form.Item name="serviceProject" label="服务项目">
            <Input allowClear placeholder="请输入服务项目" style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="certificationStatus" label="认证状态">
            <Select options={certificationStatusOptions} style={{ width: 120 }} />
          </Form.Item>
          <Form.Item name="securityDepositPaid" label="保证金是否缴纳">
            <Select options={securityDepositOptions} style={{ width: 140 }} />
          </Form.Item>
          <Form.Item name="accountStatus" label="账号状态">
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              options={accountStatusOptions}
              onChange={handleAccountStatusChange}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Card size="small">
              <Statistic title="当前查询总数" value={stats.totalCount} suffix="条" />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Card size="small">
              <Statistic title="已认证" value={stats.certifiedCount} suffix="人" valueStyle={{ color: '#52c41a' }} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Card size="small">
              <Statistic title="未认证" value={stats.uncertifiedCount} suffix="人" valueStyle={{ color: '#faad14' }} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Card size="small">
              <Statistic title="已缴保证金" value={stats.depositPaidCount} suffix="人" valueStyle={{ color: '#1890ff' }} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card size="small">
              <Statistic title="未缴保证金" value={stats.depositUnpaidCount} suffix="人" valueStyle={{ color: '#ff4d4f' }} />
            </Card>
          </Col>
        </Row>

        <div ref={tableWrapRef}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={items}
            scroll={{ x: 1700 }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, pageSize) => loadData(page, pageSize),
            }}
          />
        </div>
      </Card>

      <Drawer
        title="服务人员详情"
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        size={920}
      >
        {!currentDetail ? null : (
          <Space orientation="vertical" size={24} style={{ width: '100%' }}>
            <Card loading={detailLoading} size="small" title="基础信息">
              <Descriptions column={2} size="small">
                <Descriptions.Item label="头像">
                  <Avatar size={56} src={currentDetail.avatar ? normalizeFileUrl(currentDetail.avatar) : undefined}>
                    {currentDetail.name?.slice(0, 1)}
                  </Avatar>
                </Descriptions.Item>
                <Descriptions.Item label="姓名">{currentDetail.name || '-'}</Descriptions.Item>
                <Descriptions.Item label="登录账号">{currentDetail.username || '-'}</Descriptions.Item>
                <Descriptions.Item label="手机号">{currentDetail.phone || '-'}</Descriptions.Item>
                <Descriptions.Item label="微信昵称">{currentDetail.wechatNickname || '-'}</Descriptions.Item>
                <Descriptions.Item label="服务等级">{currentDetail.levelName || '-'}</Descriptions.Item>
                <Descriptions.Item label="认证状态">
                  {renderCertificationStatus(currentDetail.certificationStatus)}
                </Descriptions.Item>
                <Descriptions.Item label="账号状态">
                  {renderAccountStatus(currentDetail.accountStatus)}
                </Descriptions.Item>
                <Descriptions.Item label="保证金状态">
                  <Tag color={currentDetail.securityDepositPaid ? 'success' : 'default'}>
                    {currentDetail.securityDepositPaid ? '已缴纳' : '未缴纳'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="已缴保证金">
                  {moneyFormatter(currentDetail.securityDepositAmount)}
                </Descriptions.Item>
                <Descriptions.Item label="可退保证金">
                  {moneyFormatter(currentDetail.securityDepositRefundableAmount)}
                </Descriptions.Item>
                <Descriptions.Item label="提交时间">{currentDetail.certificationSubmittedAt || '-'}</Descriptions.Item>
                <Descriptions.Item label="审核时间" span={2}>{currentDetail.certificationReviewedAt || '-'}</Descriptions.Item>
                <Descriptions.Item label="服务区域" span={2}>
                  {currentDetail.serviceRegion || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="详细地址" span={2}>
                  {currentDetail.serviceAddressDetail || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="服务项目" span={2}>
                  {renderProjects(currentDetail.serviceProjects, currentDetail.serviceProjectsText)}
                </Descriptions.Item>
                <Descriptions.Item label="服务经验" span={2}>
                  {currentDetail.serviceExperience || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="可服务宠物" span={2}>
                  {renderTextTags(currentDetail.petTypes)}
                </Descriptions.Item>
                <Descriptions.Item label="特殊能力" span={2}>
                  {renderTextTags(currentDetail.specialAbilities)}
                </Descriptions.Item>
                <Descriptions.Item label="个人简介" span={2}>
                  {currentDetail.bio || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="审核备注" span={2}>
                  {currentDetail.certificationRemark || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card loading={detailLoading} size="small" title="认证材料">
              <Space size={16} wrap>
                {renderImageCard('身份证正面', currentDetail.idCardFrontUrl)}
                {renderImageCard('身份证反面', currentDetail.idCardBackUrl)}
                {renderVideoCard('视频认证', currentDetail.extraMaterialUrl)}
              </Space>
            </Card>

            <Card loading={detailLoading} size="small" title="附加信息">
              <Descriptions column={2} size="small">
                <Descriptions.Item label="App 类型">{currentDetail.appType || '-'}</Descriptions.Item>
                <Descriptions.Item label="最后登录时间">{currentDetail.lastLoginAt || '-'}</Descriptions.Item>
                <Descriptions.Item label="最后登录 IP">{currentDetail.lastLoginIp || '-'}</Descriptions.Item>
                <Descriptions.Item label="通知开关">{currentDetail.notifyEnabled ? '开启' : '关闭'}</Descriptions.Item>
                <Descriptions.Item label="接单模式">{currentDetail.orderMode ? '自动' : '手动'}</Descriptions.Item>
                <Descriptions.Item label="自动接单">{currentDetail.autoAcceptOrder ? '开启' : '关闭'}</Descriptions.Item>
                <Descriptions.Item label="接单时段">
                  {currentDetail.orderStartTime && currentDetail.orderEndTime
                    ? `${currentDetail.orderStartTime} - ${currentDetail.orderEndTime}`
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="创建时间">{currentDetail.createdAt || '-'}</Descriptions.Item>
                <Descriptions.Item label="更新时间">{currentDetail.updatedAt || '-'}</Descriptions.Item>
                <Descriptions.Item label="经纬度">
                  {currentDetail.serviceLatitude || currentDetail.serviceLongitude
                    ? `${currentDetail.serviceLatitude}, ${currentDetail.serviceLongitude}`
                    : '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Popconfirm
                title={currentDetail.accountStatus === 'active' ? '确定禁用该服务人员吗？' : '确定启用该服务人员吗？'}
                onConfirm={() => handleToggleStatus(currentDetail)}
                okButtonProps={{ loading: statusSubmitting }}
              >
                <Button danger={currentDetail.accountStatus === 'active'} icon={<StopOutlined />}>
                  {currentDetail.accountStatus === 'active' ? '禁用服务人员' : '启用服务人员'}
                </Button>
              </Popconfirm>
            </div>
          </Space>
        )}
      </Drawer>

      <Drawer
        title={`结算详情${settlementRecord?.name ? ` - ${settlementRecord.name}` : ''}`}
        open={settlementOpen}
        onClose={() => {
          setSettlementOpen(false);
          setSettlementRecord(null);
          setSettlementItems([]);
        }}
        size={1100}
      >
        <Form form={settlementSearchForm} layout="inline" style={{ marginBottom: 16, rowGap: 12 }}>
          <Form.Item name="orderNo" label="订单号">
            <Input allowClear placeholder="主订单号 / 子订单号" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="customerName" label="客户姓名">
            <Input allowClear placeholder="请输入客户姓名" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="customerPhone" label="客户电话">
            <Input allowClear placeholder="请输入客户电话" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="dateRange" label="时间范围">
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  if (settlementRecord) {
                    void loadSettlementData(settlementRecord, 1, settlementPagination.pageSize);
                  }
                }}
              >
                查询
              </Button>
              <Button
                onClick={() => {
                  settlementSearchForm.resetFields();
                  if (settlementRecord) {
                    void loadSettlementData(settlementRecord, 1, settlementPagination.pageSize);
                  }
                }}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
        <Table
          rowKey="id"
          loading={settlementLoading}
          columns={settlementColumns}
          dataSource={settlementItems}
          scroll={{ x: 1600 }}
          pagination={{
            current: settlementPagination.page,
            pageSize: settlementPagination.pageSize,
            total: settlementPagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              if (settlementRecord) {
                void loadSettlementData(settlementRecord, page, pageSize);
              }
            },
          }}
        />
      </Drawer>

      <Drawer
        title={`提现详情${withdrawRecord?.name ? ` - ${withdrawRecord.name}` : ''}`}
        open={withdrawOpen}
        onClose={() => {
          setWithdrawOpen(false);
          setWithdrawRecord(null);
          setWithdrawItems([]);
        }}
        size={1000}
      >
        <Form form={withdrawSearchForm} layout="inline" style={{ marginBottom: 16, rowGap: 12 }}>
          <Form.Item name="dateRange" label="提现时间范围">
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  if (withdrawRecord) {
                    void loadWithdrawData(withdrawRecord, 1, withdrawPagination.pageSize);
                  }
                }}
              >
                查询
              </Button>
              <Button
                onClick={() => {
                  withdrawSearchForm.resetFields();
                  if (withdrawRecord) {
                    void loadWithdrawData(withdrawRecord, 1, withdrawPagination.pageSize);
                  }
                }}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
        <Table
          rowKey="id"
          loading={withdrawLoading}
          columns={withdrawColumns}
          dataSource={withdrawItems}
          scroll={{ x: 1500 }}
          pagination={{
            current: withdrawPagination.page,
            pageSize: withdrawPagination.pageSize,
            total: withdrawPagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              if (withdrawRecord) {
                void loadWithdrawData(withdrawRecord, page, pageSize);
              }
            },
          }}
        />
      </Drawer>

      <Modal
        title="编辑服务人员"
        open={editOpen}
        onOk={() => {
          void handleEditSubmit();
        }}
        okText="保存"
        cancelText="取消"
        confirmLoading={editSubmitting}
        onCancel={() => {
          setEditOpen(false);
          setEditingDetail(null);
          editForm.resetFields();
        }}
        width={760}
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical">
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item
              name="name"
              label="姓名"
              style={{ flex: 1 }}
              rules={[{ required: true, message: '请输入姓名' }]}
            >
              <Input maxLength={100} placeholder="请输入姓名" />
            </Form.Item>
            <Form.Item name="phone" label="手机号" style={{ flex: 1 }}>
              <Input maxLength={20} placeholder="请输入手机号" />
            </Form.Item>
            <Form.Item name="levelName" label="服务等级" style={{ flex: 1 }}>
              <Input maxLength={50} placeholder="请输入服务等级" />
            </Form.Item>
          </Space>

          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="serviceProvince" label="服务省份" style={{ flex: 1 }}>
              <Input maxLength={50} placeholder="省份" />
            </Form.Item>
            <Form.Item name="serviceCity" label="服务城市" style={{ flex: 1 }}>
              <Input maxLength={50} placeholder="城市" />
            </Form.Item>
            <Form.Item name="serviceDistrict" label="服务区县" style={{ flex: 1 }}>
              <Input maxLength={50} placeholder="区县" />
            </Form.Item>
          </Space>

          <Form.Item name="serviceAddressDetail" label="详细地址">
            <Input maxLength={255} placeholder="请输入详细地址" />
          </Form.Item>

          <Form.Item name="serviceProjects" label="服务项目">
            <Input.TextArea rows={2} maxLength={255} showCount placeholder="请输入服务分类ID，多个用逗号分隔" />
          </Form.Item>

          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="petTypes" label="可服务宠物" style={{ flex: 1 }}>
              <Input maxLength={255} placeholder="多个用逗号分隔" />
            </Form.Item>
            <Form.Item name="specialAbilities" label="特殊能力" style={{ flex: 1 }}>
              <Input maxLength={255} placeholder="多个用逗号分隔" />
            </Form.Item>
          </Space>

          <Form.Item name="serviceExperience" label="服务经验">
            <Input.TextArea rows={2} maxLength={500} showCount placeholder="请输入服务经验" />
          </Form.Item>

          <Space size={16} style={{ width: '100%' }}>
            <Form.Item
              name="orderStartTime"
              label="接单开始时间"
              style={{ flex: 1 }}
              rules={[
                { required: true, message: '请输入接单开始时间' },
                { pattern: /^([01]\d|2[0-3]):[0-5]\d$/, message: '格式应为 HH:mm' },
              ]}
            >
              <Input placeholder="08:00" />
            </Form.Item>
            <Form.Item
              name="orderEndTime"
              label="接单结束时间"
              style={{ flex: 1 }}
              rules={[
                { required: true, message: '请输入接单结束时间' },
                { pattern: /^([01]\d|2[0-3]):[0-5]\d$/, message: '格式应为 HH:mm' },
              ]}
            >
              <Input placeholder="21:00" />
            </Form.Item>
          </Space>

          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="notifyEnabled" label="通知开关" style={{ flex: 1 }}>
              <Radio.Group>
                <Radio.Button value>开启</Radio.Button>
                <Radio.Button value={false}>关闭</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item name="orderMode" label="接单开关" style={{ flex: 1 }}>
              <Radio.Group>
                <Radio.Button value>开启</Radio.Button>
                <Radio.Button value={false}>关闭</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item name="autoAcceptOrder" label="自动接单" style={{ flex: 1 }}>
              <Radio.Group>
                <Radio.Button value>开启</Radio.Button>
                <Radio.Button value={false}>关闭</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Space>

          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="securityDepositPaid" label="保证金支付开关" style={{ flex: 1 }}>
              <Radio.Group>
                <Radio.Button value>已支付</Radio.Button>
                <Radio.Button value={false}>未支付</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item name="accountStatus" label="账号开关" style={{ flex: 1 }}>
              <Radio.Group>
                <Radio.Button value="active">开启</Radio.Button>
                <Radio.Button value="inactive">关闭</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item name="certificationStatus" label="认证开关" style={{ flex: 1 }}>
              <Radio.Group>
                <Radio.Button value="approved">已认证</Radio.Button>
                <Radio.Button value="reviewing">未认证</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Space>

          <Form.Item name="bio" label="个人简介">
            <Input.TextArea rows={3} maxLength={500} showCount placeholder="请输入个人简介" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="保证金扣除"
        open={deductOpen}
        onOk={() => {
          void handleDepositDeductSubmit();
        }}
        okText="确认扣除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
        confirmLoading={deductSubmitting}
        onCancel={() => {
          setDeductOpen(false);
          setDeductRecord(null);
          deductForm.resetFields();
        }}
        destroyOnHidden
      >
        <Form form={deductForm} layout="vertical">
          <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="服务人员">{deductRecord?.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="可退保证金">
              {moneyFormatter(deductRecord?.securityDepositRefundableAmount)}
            </Descriptions.Item>
          </Descriptions>
          <Form.Item
            name="amount"
            label="扣除金额"
            rules={[
              { required: true, message: '请输入扣除金额' },
              {
                validator: (_, value) => {
                  const amount = Number(value || 0);
                  const maxAmount = Number(deductRecord?.securityDepositRefundableAmount || 0);
                  if (amount <= 0) {
                    return Promise.reject(new Error('扣除金额必须大于0'));
                  }
                  if (amount > maxAmount) {
                    return Promise.reject(new Error('扣除金额不能大于可退保证金'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              min={0.01}
              max={Number(deductRecord?.securityDepositRefundableAmount || 0)}
              precision={2}
              style={{ width: '100%' }}
              placeholder="请输入扣除金额"
            />
          </Form.Item>
          <Form.Item
            name="title"
            label="流水标题"
            rules={[{ required: true, message: '请输入流水标题' }]}
          >
            <Input maxLength={100} placeholder="请输入服务人员端显示的流水标题" />
          </Form.Item>
          <Form.Item name="remark" label="扣除备注">
            <Input.TextArea rows={3} maxLength={500} showCount placeholder="请输入扣除原因" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="视频认证预览"
        open={videoPreviewOpen}
        footer={null}
        onCancel={() => {
          setVideoPreviewOpen(false);
          setVideoPreviewUrl('');
        }}
        width={720}
        destroyOnHidden
      >
        {videoPreviewUrl ? (
          <video
            src={videoPreviewUrl}
            controls
            preload="metadata"
            style={{ width: '100%', maxHeight: '70vh', borderRadius: 8, background: '#000' }}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default StaffManagement;
